import React, { useState, useEffect } from 'react';
import { DataGrid } from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/button';
import { Save, Edit, Eye, Download, FileSpreadsheet, Plus, Trash2 } from 'lucide-react';

// Handle ResizeObserver errors
const originalError = console.error;
console.error = (...args) => {
  if (args[0] && args[0].toString().includes('ResizeObserver loop completed with undelivered notifications')) {
    return;
  }
  originalError(...args);
};

// Also handle window errors
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    if (e.message && e.message.includes('ResizeObserver loop completed with undelivered notifications')) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });
}

const ExcelViewer = ({ file, content, projectId, myProfile, onSave, onDownload, isLoading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [originalData, setOriginalData] = useState([]);

  useEffect(() => {
    if (content) {
      parseSpreadsheetContent();
    }
  }, [content]);

  const parseSpreadsheetContent = () => {
    try {
      let data = [];
      
      if (file.file_type.toLowerCase() === 'csv') {
        // Parse CSV
        const lines = content.split('\n').filter(line => line.trim());
        data = lines.map(line => line.split(',').map(cell => cell.trim()));
      } else {
        // For Excel files, we'll simulate parsing since we're working with text content
        // In production, you'd handle binary Excel files properly
        const lines = content.split('\n').filter(line => line.trim());
        data = lines.map(line => line.split('\t').map(cell => cell.trim()));
      }

      if (data.length === 0) return;

      // Create columns based on the first row or generate default columns
      const headers = data[0];
      const cols = headers.map((header, index) => ({
        key: `col_${index}`,
        name: header || `Column ${index + 1}`,
        editable: true,
        resizable: true,
        width: 120
      }));

      // Add extra columns for expansion
      for (let i = headers.length; i < headers.length + 5; i++) {
        cols.push({
          key: `col_${i}`,
          name: `Column ${i + 1}`,
          editable: true,
          resizable: true,
          width: 120
        });
      }

      setColumns(cols);

      // Create rows
      const processedRows = [];
      for (let rowIndex = 0; rowIndex < Math.max(data.length, 20); rowIndex++) {
        const row = { id: rowIndex };
        const rowData = data[rowIndex] || [];
        
        cols.forEach((col, colIndex) => {
          row[col.key] = rowData[colIndex] || '';
        });
        
        processedRows.push(row);
      }

      setRows(processedRows);
      setOriginalData(processedRows);
      
    } catch (error) {
      console.error('Error parsing spreadsheet:', error);
      // Fallback to simple table
      const fallbackRows = [{ id: 0, col_0: content }];
      const fallbackCols = [{ key: 'col_0', name: 'Data', editable: true }];
      setRows(fallbackRows);
      setColumns(fallbackCols);
    }
  };

  const handleRowsChange = (updatedRows) => {
    setRows(updatedRows);
  };

  const handleSave = async () => {
    try {
      // Convert rows back to CSV format
      const csvContent = rows
        .map(row => 
          columns.map(col => row[col.key] || '').join(',')
        )
        .filter(row => row.trim() !== ','.repeat(columns.length - 1))
        .join('\n');

      await onSave(csvContent);
      setOriginalData([...rows]);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving spreadsheet:', error);
    }
  };

  const addRow = () => {
    const newRow = { id: rows.length };
    columns.forEach(col => {
      newRow[col.key] = '';
    });
    setRows([...rows, newRow]);
  };

  const addColumn = () => {
    const newColIndex = columns.length;
    const newColumn = {
      key: `col_${newColIndex}`,
      name: `Column ${newColIndex + 1}`,
      editable: true,
      resizable: true,
      width: 120
    };
    
    setColumns([...columns, newColumn]);
    
    // Add empty values for the new column in all rows
    const updatedRows = rows.map(row => ({
      ...row,
      [newColumn.key]: ''
    }));
    setRows(updatedRows);
  };

  const hasChanges = JSON.stringify(rows) !== JSON.stringify(originalData);

  return (
    <div className="space-y-4">
      {/* Spreadsheet Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <FileSpreadsheet className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-gray-900">{file.original_filename}</h3>
            <p className="text-sm text-gray-500">
              Spreadsheet • {rows.length} rows × {columns.length} columns • {Math.round(file.file_size / 1024)} KB
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          ) : (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  setRows([...originalData]);
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                size="sm"
                onClick={handleSave}
                disabled={isLoading || !hasChanges}
                className="bg-cyan-500 hover:bg-cyan-600"
              >
                <Save className="h-4 w-4 mr-1" />
                Save Changes
              </Button>
            </>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={onDownload}
          >
            <Download className="h-4 w-4 mr-1" />
            Download
          </Button>
        </div>
      </div>

      {/* Editing Tools */}
      {isEditing && (
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          <Button 
            variant="outline" 
            size="sm"
            onClick={addRow}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Row
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={addColumn}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Column
          </Button>
          <div className="ml-auto text-sm text-gray-600">
            {hasChanges && (
              <span className="text-orange-600 font-medium">● Unsaved changes</span>
            )}
          </div>
        </div>
      )}

      {/* Spreadsheet Grid */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <div style={{ height: '500px' }}>
          <DataGrid
            columns={columns}
            rows={rows}
            onRowsChange={isEditing ? handleRowsChange : undefined}
            className="rdg-light"
            style={{ 
              fontSize: '13px',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
            headerRowHeight={40}
            rowHeight={35}
            enableVirtualization={true}
            rowKeyGetter={(row) => row.id}
          />
        </div>
      </div>

      {/* Spreadsheet Statistics */}
      <div className="flex items-center justify-between text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center gap-4">
          <span>Rows: {rows.filter(row => Object.values(row).some(val => val && val !== '')).length}</span>
          <span>Columns: {columns.length}</span>
          <span>Cells: {rows.length * columns.length}</span>
        </div>
        <div>
          {isEditing ? (
            <span className="text-orange-600 font-medium">● Editing mode</span>
          ) : (
            <span className="text-green-600">● View mode</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelViewer;